import { HttpClient } from '@angular/common/http';
import { ApplicationRef, createComponent, EnvironmentInjector, Injectable } from '@angular/core';
import { CalendarEvent } from 'calendar';
import { BehaviorSubject, catchError, concatMap, EMPTY, finalize, ignoreElements, map, Observable, of, shareReplay, Subject, switchMap, takeWhile, tap, throwError } from 'rxjs';
import { Logger, User } from 'shared';

import { AuthService } from 'auth';
import { InvitationDialogAction, InvitationDialogComponent } from 'invitation';
import { Invitation } from '../models/invitation';
import { InvitationError } from '../models/invitation-error';
import { Relationship } from '../models/relationship';

@Injectable({
  providedIn: 'root'
})
export class InvitationService {
    
  protected relationship$ = new Subject();
  public relationship = this.relationship$.asObservable();

  protected logger = new Logger('InvitationService');
  constructor(private httpClient: HttpClient, 
              private authService: AuthService,
              private appRef: ApplicationRef,
              private injector: EnvironmentInjector) {}

  createToken(type: number, data: number): Observable<{ token: string }> {
    return this.httpClient.post<{ token: string }>(
      '/invitation/token/create',
      { type, data }
    );
  }

  decodeToken(token: string): Observable<Invitation> {
    return this.httpClient.post<Invitation>('/invitation/token/decode', { token });
  }

  accept(token: string): Observable<Relationship> {
    return this.httpClient.post<Relationship>('/invitation/accept', { token });
  }

  decline(token: string): Observable<Relationship> {
    return this.httpClient.post<Relationship>('/invitation/decline', { token });
  }
  
  processInvitationToken(
    invitationToken: string,
    currentUserId = this.authService.getUser()?.id,
  ): Observable<Invitation> {
    return this.decodeToken(invitationToken).pipe(
      map(invitation => this.validateInvitation(invitation, currentUserId)),
      switchMap(invitation => {
        // Mutable reference — accumulates updates
        let current = { ...invitation }; // or mutate initialInvitation directly
        return this.displayInvitationDialog(invitation, invitationToken, this.authService.getUser()).pipe(
          concatMap(action => {
            if (action.type === 'join') {
              return of(current);
            }
            if (action.type == 'accept' || action.type == 'decline') {
              const action$ = action.type === 'accept'
                ? this.accept(invitationToken)
                : this.decline(invitationToken);
    
              return action$.pipe(
                tap(relationship => {
                  current = {
                    ...current,
                    relationship
                  };
                  this.relationship$.next(relationship);
                }),
                ignoreElements(),
                // catchError(err => { console.warn(err); return EMPTY; })
              );
            }
            // if (action.type === 'close') {
              return EMPTY;
            // }
          }),
          // takeWhile(action => action.type !== 'close', true)
        );
      }),
      catchError(err => {
        this.logger.error(`Invitation processing failed for token ${invitationToken.slice(0, 8)}...`, err);
        return throwError(() => err);
      })
    );
  }

  private validateInvitation(
    invitation: Invitation | undefined,
    currentUserId: number | undefined
  ): Invitation {
    if (!invitation) {
      throw InvitationError.empty();
    }

    const referrer = invitation.referrer;
    const event = invitation.data as any;

    if (!referrer?.full_name || !event?.name) {
      throw InvitationError.incomplete(invitation);
    }

    if (currentUserId && referrer.id == currentUserId) {
      throw InvitationError.selfInvitation(invitation);
    }

    if (!CalendarEvent.isConference(event)) {
      throw InvitationError.notConference(invitation);
    }

    // if (invitation.relationship?.acknowledged) {
    //   throw InvitationError.alreadyAcknowledged(invitation);
    // }

    return invitation;
  }
  

  displayInvitationDialog(invitation: Invitation, invitationToken: string, user: User | null): Observable<InvitationDialogAction> {
    const hostElement = document.createElement('div');
    document.body.appendChild(hostElement);
    const componentRef = createComponent(InvitationDialogComponent, {
      environmentInjector: this.injector,
      hostElement,
    });
    componentRef.setInput('invitation', invitation);
    componentRef.setInput('invitationToken', invitationToken);
    componentRef.setInput('user', user);
    const actions$ = componentRef.instance.actions$.pipe(
      finalize(() => cleanup())
    )

    this.appRef.attachView(componentRef.hostView);
    const cleanup = () => {
      // give Bootstrap time to finish hide animation & remove backdrop
      // aggresive/immediate destruction leaves the backdrop element in the DOM (and visible on the screen)
      setTimeout(() => {
        componentRef.destroy();
        this.appRef.detachView(componentRef.hostView);
        document.body.removeChild(hostElement);
      }, 100);
    }
    return actions$;
  }
}

 // private displayInvitationDialog(
  //   invitation: Invitation,
  //   invitationToken: string
  // ): Observable<InvitationDialogResult> {
  //   const referrerName = invitation.referrer.full_name!;
  //   const eventName = invitation.data.name;
  //   const conferenceDate = moment(invitation.data.timeFrom).format('LLL');

  //   const dialogRef = this.dialogService.open({
  //     title: 'invitation.title',
  //     message: this.translateService.translate('invitation.joinConference', {
  //       referrerName,
  //       conferenceName: eventName,
  //       conferenceDate
  //     }),
  //     confirmLabel: 'actions.accept',
  //     cancelLabel: 'actions.decline',
  //     imageUrl: '/assets/images/default-avatar.jpg',
  //     confirmColor: 'primary',
  //     size: 'lg',
  //   });

  //   return new Observable<InvitationDialogResult>(subscriber => {
  //     const subscription = dialogRef.subscribe({
  //       next: (accepted: boolean) => {
  //         subscriber.next(
  //           accepted
  //             ? { type: 'accept',   token: invitationToken, invitation }
  //             : { type: 'decline',  token: invitationToken, invitation }
  //         );
  //         subscriber.complete();
  //       },
  //       complete: () => {
  //         // dismissed without choice
  //         subscriber.next({ type: 'dismissed', invitation });
  //         subscriber.complete();
  //       },
  //       error: error => {
  //         this.logger.error('Dialog error', error);
  //         subscriber.error(error);
  //       }
  //     });

  //     return () => subscription.unsubscribe();
  //   });
  // }

  // displayInvitationDialog(
  //   invitation: Invitation,
  //   invitationToken: string,
  //   currentUserId: string | undefined,
  // ): Observable<Invitation> {
  //   return new Observable(subscriber => {
  //     const error = (message: string, invitation?: Invitation) => {
  //       this.logger.warn(message);
  //       subscriber.error({message, invitation});
  //       subscriber.complete();
  //       return (() => {}) as TeardownLogic;
  //     }
  //     if (invitation) {
  //       const referrer = invitation.referrer;
  //       const event    = invitation.data;
  //       let referrerName; let eventName;
  //       if ((referrerName = referrer.full_name) && (eventName = event.name)) {
  //         if (referrer.id != currentUserId) {
  //           if (CalendarEvent.isConference(event)) {
  //             const connected = invitation.relationship?.acknowledged;
  //             if (!connected) {
  //               const dialogRef = this.dialogService.open({
  //                 title: 'invitation.title',
  //                 message: this.translateService.translate('invitation.joinConference', {
  //                   referrerName,
  //                   conferenceName: eventName,
  //                   conferenceDate: moment(event.timeFrom).format('LLL')
  //                 }),
  //                 confirmLabel: 'actions.accept',
  //                 cancelLabel: 'actions.decline',
  //                 imageUrl: '/assets/images/default-avatar.jpg',
  //                 confirmColor: 'primary',
  //                 size: 'lg',
  //               });
  //               return dialogRef.subscribe({
  //                 next: (accepted: boolean) => {
  //                   const action$ = accepted
  //                     ? this.accept(invitationToken)
  //                     : this.decline(invitationToken);
  //                   action$.subscribe({
  //                     next: (relationship) => {
  //                       this.logger.debug('Invitation processed', relationship);
  //                       const result = {...invitation, relationship};
  //                       subscriber.next(result);
  //                       subscriber.complete();
  //                     },
  //                     error: (error) => {
  //                       this.logger.error('Invitation failed', error);
  //                       subscriber.error(error);
  //                     }
  //                   });
  //                 },
  //                 // dialog dismissed without choice
  //                 complete: () => {
  //                   // do not emit here - complete is called before action$ emits
  //                   // subscriber.next(invitation);
  //                   // subscriber.complete();
  //                 }
  //               }); 
  //             } else {
  //               return error('Invitation is already acknowledged')
  //             }
  //           } else {
  //             return error('Invitation is not for conference', invitation);  
  //           }
  //         } else {
  //           return error('Self-invitation ignored', invitation);
  //         }
  //       } else {
  //         return error('Incomplete invitation', invitation);
  //       }
  //     } else {
  //       return error('Empty invitation');
  //     }
  //   });
  // }